import React, {useCallback, useMemo} from 'react';
import styled from "styled-components";
import {TaxonomyTreeFilterUI} from "./TaxonomyTreeFilterUI";
import {ManufacturerFilterUI} from "./ManufacturerFilterUI";
import {SpeciesFilterUI} from "./SpeciesFilterUI";
import {CharacteristicsFilterUI} from "./CharacteristicsFilterUI";

const MenuContainer = styled.div`
    width: 100%;
    height: auto;
    position: relative;
    padding: 1rem;
    margin-bottom: 1.5rem;
    border: 1.1px solid #D0D0D0;

    &.taxonomy {
        overflow-wrap: anywhere;
        border: none;
        padding: 0;
    }
`;

const StylesContainer = styled.div`
    .taxonomy .MuiAccordion-gutters:before {
        display: none;
    }

    .taxonomy .form-select {
        border-radius: 0.75rem;
        height: 48px;
    }

    .taxonomy .subcategory {
        margin-top: 0;
    }

    .taxonomy .subsubcategory {
        margin-top: 0;
        border-top: 0;
    }

    .taxonomy .taxonomy-filters {
        margin-top: 2rem;
    }

    .taxonomy .taxonomy-dropdowns {
        margin-bottom: 1.5rem;
    }

    /* Tarjeta redondeada y suave, como el resto de la web (buscador, tarjetas de producto). */
    .categories-tree {
        margin-bottom: 1.5rem;
        padding: 0.75rem;
        border: 1px solid rgb(222, 226, 230);
        border-radius: 1rem;
    }

    .categories-tree div,
    .categories-tree select {
        border: none;
        background-color: transparent;
    }

    .accordion {
        border: 1px solid rgb(222, 226, 230);
        border-radius: 1rem !important;
        overflow: hidden;
        box-shadow: none;
    }

    .accordion .MuiButtonBase-root.MuiAccordionSummary-root.MuiAccordionSummary-gutters {
        padding-right: 10px;
    }

    /* Checkboxes y radios en el color del tenant. */
    input[type="checkbox"],
    input[type="radio"] {
        accent-color: var(--bs-primary);
    }

    .form-check-input:checked {
        background-color: var(--bs-primary);
        border-color: var(--bs-primary);
    }

    /* Filas de opciones con respiro y hover suave. */
    .form-check {
        padding-top: 0.15rem;
        padding-bottom: 0.15rem;
        border-radius: 0.5rem;
    }

    .form-check:hover {
        background: rgba(0, 0, 0, 0.03);
    }

    /* Listas largas (fabricantes) con scroll propio y fino. */
    .MuiAccordionDetails-root {
        max-height: 320px;
        overflow-y: auto;
        scrollbar-width: thin;
    }
`;

const ChipsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
    margin-bottom: 1rem;
`;

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    background: var(--bs-primary);
    color: white;
    border-radius: 999px;
    padding: 0.2rem 0.4rem 0.2rem 0.75rem;
    font-size: 0.78rem;
    font-weight: 600;
    max-width: 100%;

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    button {
        border: none;
        background: rgba(255, 255, 255, 0.25);
        color: white;
        border-radius: 999px;
        width: 1.1rem;
        height: 1.1rem;
        line-height: 1;
        font-size: 0.7rem;
        padding: 0;
        flex-shrink: 0;
        cursor: pointer;

        &:hover {
            background: rgba(255, 255, 255, 0.45);
        }
    }
`;

const ClearAll = styled.button`
    border: none;
    background: none;
    color: var(--bs-primary);
    font-size: 0.78rem;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
`;

const FILTER_KEYS = Object.freeze({
    taxonomyTree: 'taxonomyTree',
    manufacturerIds: 'manufacturerIds',
    species: 'species',
    characteristics: 'characteristics',
    // No es un filtro del panel, pero tiene que sobrevivir a updateFilter: si no esta en
    // allowedKeys, tocar cualquier filtro del panel borra la busqueda activa del navbar.
    search: 'search',
});


export const buildFilterPanelConfig = (options, selected, update) => [
    {
        Component: TaxonomyTreeFilterUI,
        options: options.macrofamilias,
        value: selected[FILTER_KEYS.taxonomyTree],
        onChange: update(FILTER_KEYS.taxonomyTree),
    },
    {
        Component: ManufacturerFilterUI,
        options: options.fabricantes,
        value: selected[FILTER_KEYS.manufacturerIds],
        onChange: update(FILTER_KEYS.manufacturerIds),
    },
    {
        Component: SpeciesFilterUI,
        options: options.especies,
        value: selected[FILTER_KEYS.species],
        onChange: update(FILTER_KEYS.species),
    },
    {
        Component: CharacteristicsFilterUI,
        options: options.caracteristicas,
        value: selected[FILTER_KEYS.characteristics],
        onChange: update(FILTER_KEYS.characteristics),
    },
];

// Chips de lo que hay filtrado ahora mismo, con nombres legibles y su aspa para quitarlo.
const buildActiveChips = (filterOptions, filter) => {
    const chips = [];
    const tax = filter?.taxonomyTree;
    const macro = tax?.macrofamilyId
        ? filterOptions.macrofamilias?.find(m => m.id === tax.macrofamilyId)
        : null;
    if (macro) {
        const family = tax?.familyId ? macro.familias?.find(f => f.id === tax.familyId) : null;
        const subId = tax?.subfamilyIds?.size ? Array.from(tax.subfamilyIds)[0] : null;
        const sub = subId && family ? family.subfamilias?.find(s => s.id === subId) : null;
        const label = sub?.subfamilia || family?.familia || macro.macrofamilia;
        chips.push({
            key: 'tax',
            label,
            remove: f => ({ ...f, taxonomyTree: undefined }),
        });
    }
    Array.from(filter?.manufacturerIds || []).forEach(id => {
        const fab = filterOptions.fabricantes?.find(m => m.id === id);
        chips.push({
            key: `fab-${id}`,
            label: fab?.nombrerazonsocial || `Fabricante ${id}`,
            remove: f => {
                const next = new Set(f.manufacturerIds);
                next.delete(id);
                return { ...f, manufacturerIds: next.size ? next : undefined };
            },
        });
    });
    Array.from(filter?.species || []).forEach(specie => {
        chips.push({
            key: `esp-${specie}`,
            label: specie,
            remove: f => {
                const next = new Set(f.species);
                next.delete(specie);
                return { ...f, species: next.size ? next : undefined };
            },
        });
    });
    if (filter?.characteristics?.prescription != null) {
        chips.push({
            key: 'presc',
            label: filter.characteristics.prescription
                ? 'Con prescripción'
                : 'Sin prescripción',
            remove: f => ({ ...f, characteristics: undefined }),
        });
    }
    return chips;
};

export const FilterPanel = ({filterOptions, filter, onFilterChange}) => {

    const updateFilter = useCallback((key) => (value) => {
        const allowedKeys = Object.values(FILTER_KEYS);

        const newFilter = {
            ...Object.fromEntries(Object.entries(filter).filter(([k]) => allowedKeys.includes(k))),
            [key]: value,
        }
        onFilterChange(newFilter);
    }, [filter]);

    const filterPanelConfig = useMemo(
        () => buildFilterPanelConfig(filterOptions, filter, updateFilter),
        [filterOptions, filter]);

    const activeChips = useMemo(
        () => buildActiveChips(filterOptions, filter),
        [filterOptions, filter]);

    const removeChip = (chip) => {
        onFilterChange(chip.remove(filter));
    };

    const clearAll = () => {
        // La busqueda del navbar sobrevive; solo se limpian los filtros del panel.
        onFilterChange(filter?.search ? { search: filter.search } : {});
    };

    return (
        <StylesContainer>
            {activeChips.length > 0 && (
                <ChipsRow>
                    {activeChips.map(chip => (
                        <Chip key={chip.key} title={chip.label}>
                            <span>{chip.label}</span>
                            <button
                                type="button"
                                aria-label={`Quitar filtro ${chip.label}`}
                                onClick={() => removeChip(chip)}
                            >
                                ✕
                            </button>
                        </Chip>
                    ))}
                    <ClearAll type="button" onClick={clearAll}>Quitar todos</ClearAll>
                </ChipsRow>
            )}

            <MenuContainer className="taxonomy">
                {filterPanelConfig.map(({Component, options, value, onChange}, index) => (
                    <Component
                        key={index}
                        options={options}
                        value={value}
                        onChange={onChange}
                    />
                ))}
            </MenuContainer>
        </StylesContainer>
    )
};
